import ReactCalendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { isSameDay, startOfToday } from "date-fns";
import styles from "./CalendarView.module.css";

interface CalendarViewProps {
	selectedDate: Date | null;
	onSelectDate: (date: Date) => void;
	availableDates?: Date[];
}

export function CalendarView({
	selectedDate,
	onSelectDate,
	availableDates = [],
}: CalendarViewProps) {
	const today = startOfToday();

	// Custom tile content to show availability
	const tileContent = ({ date, view }: { date: Date; view: string }) => {
		if (view === "month") {
			const hasAvailability = availableDates.some((availableDate) =>
				isSameDay(date, availableDate),
			);

			if (hasAvailability && date >= today) {
				return (
					<div className={styles.availabilityIndicator}>
						<div className={styles.dot} />
					</div>
				);
			}
		}
		return null;
	};

	// Disable dates before today or dates without availability
	const tileDisabled = ({ date }: { date: Date }) => {
		if (date < today) return true;

		// If we have available dates loaded, only enable those dates
		if (availableDates.length > 0) {
			return !availableDates.some((availableDate) =>
				isSameDay(date, availableDate),
			);
		}

		return false;
	};

	// Custom tile className for styling
	const tileClassName = ({ date, view }: { date: Date; view: string }) => {
		if (view === "month") {
			const classes = [];

			if (selectedDate && isSameDay(date, selectedDate)) {
				classes.push(styles.selectedDate);
			}

			if (date < today) {
				classes.push(styles.disabledDate);
			}

			return classes.join(" ");
		}
		return "";
	};

	return (
		<div className={styles.calendarContainer}>
			<ReactCalendar
				onChange={(value) => onSelectDate(value as Date)}
				value={selectedDate}
				tileContent={tileContent}
				tileDisabled={tileDisabled}
				tileClassName={tileClassName}
				minDate={today}
				className={styles.customCalendar}
			/>
		</div>
	);
}
